import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { TableCell } from '@anytag/shared-atoms';
import { cn, formatIntNumber, formatPrice } from '@anytag/shared-utils';
import { CampaignType, InfluencerCampaignDetailByPromotion } from '@anytag/graphql';

export const useCampaignsListTable = () => {
  const { t } = useTranslation();
  const { isTalentAgencyPartner } = useUserRoles();

  const columns: ColumnDef<InfluencerCampaignDetailByPromotion>[] = [
    {
      accessorKey: 'campaign',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Campaign'),
      cell: ({ row }) => {
        const { campaignInfo } = row.original;
        const canAccessMarketplace =
          !(campaignInfo.type === CampaignType.MARKETPLACE && isTalentAgencyPartner) &&
          // no campaign ID means that current campaign is not associated with logged in user, so we should disable the links
          campaignInfo.id;

        return (
          <TableCell.Campaign
            type={campaignInfo.type}
            title={campaignInfo.title || ''}
            src={campaignInfo.thumbNail || ''}
            id={campaignInfo.id?.toString() || ''}
            mkpCampaignType={campaignInfo.mkpDetailType}
            isPostTracking={!!campaignInfo.hasPostTracking}
            className={cn(
              'w-60 border-none bg-transparent p-0',
              canAccessMarketplace ? 'pointer-events-auto' : 'pointer-events-none'
            )}
          />
        );
      }
    },
    {
      accessorKey: 'promotionMethods',
      header: t<string>('HeaderColumn.Promotion Methods'),
      cell: ({ row }) => <TableCell.Text value={row.original.campaignInfo.promotionMethod} />
    },
    {
      accessorKey: 'sales',
      header: t<string>('HeaderColumn.Sales'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number
          value={formatPrice(row.original.summary.sales, row.original.currency)}
          unit={row.original.summary.sales ? t(row.original.currency) : ''}
        />
      )
    },
    {
      accessorKey: 'clicks',
      header: t<string>('HeaderColumn.Clicks'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.clicks)} />
    },
    {
      accessorKey: 'orders',
      header: t<string>('HeaderColumn.Orders'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.orders)} />
    },
    {
      accessorKey: 'actions',
      header: t<string>('HeaderColumn.Actions'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.actions)} />
    }
  ];

  return { columns };
};
